import { useState } from 'react';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Pencil } from 'lucide-react';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SideRailLayout } from '@/layouts/side-rail.layout';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { AudienceSimpleFilterBuilder } from '@/components/audiences/audience-simple-filter-builder';
import { AudienceCreateSimplifiedHelpCard } from '@/components/audiences/audience-create-simplified-help-card';
import {
  AudienceTargetCount,
  type AudienceTargetCountStatus,
} from '@/components/audiences/audience-target-count';
import { AudienceFilterDonut } from '@/components/audiences/audience-filter-donut';
import { AudienceEditNameDialog } from '@/components/audiences/audience-edit-name-dialog';
import { AudienceCreateModeSwitch } from '@/components/audiences/audience-create-mode-switch';
import { LoaderButton } from '@/components/loader-button';

export interface AudienceCreateSimplifiedPresenterProps {
  tenantSlug: string;
  audienceId?: string;
  locale: Locales;
  name: string;
  onNameChange: (name: string) => void;
  nameError?: string;
  targetCountStatus: AudienceTargetCountStatus;
  targetUsersCount: number | undefined;
  totalUserbaseCount: number;
  onRecalculateTargetCount: () => void;
  onCancel: () => void;
  onCreate: () => void;
  isCreating: boolean;
  isCreateDisabled: boolean;
  isSaveNameDialogOpen: boolean;
  onSaveNameDialogOpenChange: (open: boolean) => void;
  saveNameDialogName: string;
  onSaveNameConfirm: (name: string) => void;
}

export function AudienceCreateSimplifiedPresenter({
  tenantSlug,
  audienceId,
  locale,
  name,
  onNameChange,
  nameError,
  targetCountStatus,
  targetUsersCount,
  totalUserbaseCount,
  onRecalculateTargetCount,
  onCreate,
  isCreating,
  isCreateDisabled,
  isSaveNameDialogOpen,
  onSaveNameDialogOpenChange,
  saveNameDialogName,
  onSaveNameConfirm,
}: AudienceCreateSimplifiedPresenterProps) {
  const { LL } = useI18nContext();
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [isHelpCardVisible, setIsHelpCardVisible] = useState(true);
  const isEditMode = Boolean(audienceId);
  const backHref = isEditMode
    ? `/${tenantSlug}/audiences/${audienceId}`
    : `/${tenantSlug}/audiences`;

  return (
    <SideRailLayout
      className="!p-0"
      sideRail={
        <div className="flex h-full min-h-0 flex-col items-center p-6 pt-[30%]">
          <AudienceFilterDonut
            className="w-full"
            isLoading={targetCountStatus === 'loading'}
            targetUsersCount={
              targetCountStatus === 'empty' ? undefined : targetUsersCount
            }
            totalUserbaseCount={totalUserbaseCount}
            locale={locale}
          />
        </div>
      }
    >
      <CenteredContentLayout>
        <Header className="flex w-full items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <PageBreadcrumb
              items={[
                {
                  label: LL.audiences.title(),
                  href: `/${tenantSlug}/audiences`,
                },
                ...(isEditMode
                  ? [
                      {
                        label: name,
                        href: `/${tenantSlug}/audiences/${audienceId}`,
                      },
                    ]
                  : []),
                {
                  label: isEditMode
                    ? LL.audiences.edit.breadcrumbLabel()
                    : LL.audiences.create.breadcrumbLabel(),
                },
              ]}
            />
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <Link
                to={backHref}
                className="shrink-0"
                aria-label={
                  isEditMode
                    ? LL.audiences.details.breadcrumbLabel()
                    : LL.audiences.title()
                }
              >
                <ChevronLeft className="size-6" />
              </Link>
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <h1 className="min-w-0 truncate text-lg tracking-tight sm:text-xl">
                  {name}
                </h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={LL.audiences.create.editNameAriaLabel()}
                  onClick={() => setIsEditNameOpen(true)}
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-end sm:gap-4">
            <AudienceCreateModeSwitch
              tenantSlug={tenantSlug}
              audienceId={audienceId}
              mode="simplified"
            />
            <LoaderButton
              type="button"
              isLoading={isCreating}
              disabled={isCreateDisabled}
              onClick={onCreate}
            >
              {isCreating
                ? isEditMode
                  ? LL.audiences.edit.actions.saving()
                  : LL.audiences.create.actions.creating()
                : isEditMode
                  ? LL.audiences.edit.actions.save()
                  : LL.audiences.create.actions.create()}
            </LoaderButton>
          </div>
        </Header>

        <CenteredContent>
          <div className="flex min-w-0 flex-col gap-8 sm:gap-10">
            {nameError && (
              <p className="text-sm text-destructive entry-fade-in-up entry-delay-0">
                {nameError}
              </p>
            )}

            <AudienceTargetCount
              status={targetCountStatus}
              targetUsersCount={targetUsersCount}
              totalUserbaseCount={totalUserbaseCount}
              onRecalculate={onRecalculateTargetCount}
              locale={locale}
              className="entry-fade-in-up entry-delay-0 lg:hidden"
            />

            {isHelpCardVisible && !isEditMode ? (
              <AudienceCreateSimplifiedHelpCard
                onDismiss={() => setIsHelpCardVisible(false)}
                className="entry-fade-in-up entry-delay-0"
              />
            ) : null}

            <AudienceSimpleFilterBuilder />
          </div>
        </CenteredContent>
      </CenteredContentLayout>

      <AudienceEditNameDialog
        open={isEditNameOpen}
        onOpenChange={setIsEditNameOpen}
        name={name}
        onConfirm={onNameChange}
      />

      <AudienceEditNameDialog
        open={isSaveNameDialogOpen && !isEditMode}
        onOpenChange={onSaveNameDialogOpenChange}
        name={saveNameDialogName}
        title={LL.audiences.create.saveNameDialog.title()}
        onConfirm={onSaveNameConfirm}
      />
    </SideRailLayout>
  );
}
